import React, { useEffect, useState } from 'react';
import { Alert, Button, Descriptions, Progress, Skeleton, Tag, Typography } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  RightOutlined,
  SmileOutlined,
} from '@ant-design/icons';
import {
  getPosHubApps,
  getPosHubLocationSettings,
  getPosHubMenus,
  getPosHubStatus,
} from 'api/poshub';
import { captureException } from 'utils/errors';
import { PH } from './theme';
import { hasConfiguredBusinessHours } from './poshubOpeningHours';
import { StyledAboutTab } from './AboutTab.styles';

const STATUS_COLORS = {
  ACTIVE: 'green',
  PENDING: 'gold',
  ERROR: 'red',
  NOT_CONNECTED: 'default',
};

const STEP_STATUS = {
  DONE: 'done',
  TODO: 'todo',
  LATER: 'later',
};

function AboutTab({ restaurantId, merchant, onGoToTab, activeTab }) {
  const [status, setStatus] = useState(null);
  const [apps, setApps] = useState([]);
  const [menusCount, setMenusCount] = useState(0);
  const [openingHoursConfigured, setOpeningHoursConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!restaurantId) return;
    if (activeTab != null && activeTab !== 'about') return;
    setLoading(true);
    setError(null);
    Promise.all([
      getPosHubStatus(restaurantId),
      getPosHubApps(restaurantId).then((res) => res.data || []).catch(() => []),
      getPosHubMenus(restaurantId).then((res) => (res.data || res || []).length).catch(() => 0),
      getPosHubLocationSettings(restaurantId)
        .then((data) => data?.data?.businessHours || data?.businessHours || [])
        .catch(() => []),
    ])
      .then(([statusData, appsList, count, businessHours]) => {
        setStatus(statusData);
        setApps(Array.isArray(appsList) ? appsList : []);
        setMenusCount(typeof count === 'number' ? count : 0);
        setOpeningHoursConfigured(hasConfiguredBusinessHours(businessHours));
      })
      .catch((e) => {
        captureException(e);
        setError(e?.response?.data?.detail || 'Could not load integration details.');
        setStatus({ is_connected: false, status: 'NOT_CONNECTED' });
        setApps([]);
        setMenusCount(0);
        setOpeningHoursConfigured(false);
      })
      .finally(() => setLoading(false));
  }, [restaurantId, activeTab]);

  const hasPosAppConnected = apps.some(
    (a) => a.isPosApp && (a.status === 'COMPLETED' || a.status === 'ACTIVE'),
  );
  const hasCatalogSynced = Boolean(status?.last_menu_sync);
  const hasMenu = menusCount > 0;

  const steps = [
    {
      key: 'connect',
      title: 'Set up POS integration',
      description: 'Connect this restaurant so Nibble can sync menus and orders with your POS.',
      status: status?.is_connected ? STEP_STATUS.DONE : STEP_STATUS.TODO,
      tab: 'apps',
      cta: 'Go to Apps',
    },
    {
      key: 'pos_app',
      title: 'Connect a POS application',
      description: 'Connect a Point of Sale app (e.g. Lightspeed, Square, Clover) so your catalog can sync and orders can flow through the integration.',
      status: hasPosAppConnected ? STEP_STATUS.DONE : (status?.is_connected ? STEP_STATUS.TODO : STEP_STATUS.LATER),
      tab: 'apps',
      cta: 'Go to Apps',
    },
    {
      key: 'opening_hours',
      title: 'Setup location hours',
      description: 'Set default opening hours for this location. Menus inherit these hours unless custom availability is configured.',
      status: openingHoursConfigured ? STEP_STATUS.DONE : (status?.is_connected ? STEP_STATUS.TODO : STEP_STATUS.LATER),
      tab: 'settings',
      cta: 'Go to Settings',
    },
    {
      key: 'sync_catalog',
      title: 'Sync catalog from POS',
      description: 'Pull your products and categories from the POS into the integration via the Apps tab.',
      status: hasCatalogSynced ? STEP_STATUS.DONE : (hasPosAppConnected ? STEP_STATUS.TODO : STEP_STATUS.LATER),
      tab: 'apps',
      cta: 'Sync in Apps',
    },
    {
      key: 'create_menu',
      title: 'Create menus',
      description: 'Build menus from your catalog to define what you offer for delivery or collection.',
      status: hasMenu ? STEP_STATUS.DONE : STEP_STATUS.LATER,
      tab: 'menus',
      cta: 'Go to Menus',
    },
  ];

  const completedCount = steps.filter((s) => s.status === STEP_STATUS.DONE).length;
  const allComplete = completedCount === steps.length;

  const renderStepIcon = (stepStatus) => {
    switch (stepStatus) {
      case STEP_STATUS.DONE:
        return <CheckCircleOutlined className="step-icon done" />;
      case STEP_STATUS.TODO:
        return <ExclamationCircleOutlined className="step-icon todo" />;
      default:
        return <ClockCircleOutlined className="step-icon later" />;
    }
  };

  const renderStepStatusLabel = (stepStatus) => {
    switch (stepStatus) {
      case STEP_STATUS.DONE:
        return <span className="step-label done">Done</span>;
      case STEP_STATUS.TODO:
        return <span className="step-label todo">To do</span>;
      default:
        return <span className="step-label later">You can do this later</span>;
    }
  };

  if (loading) {
    return (
      <StyledAboutTab>
        <Skeleton active paragraph={{ rows: 6 }} />
      </StyledAboutTab>
    );
  }

  return (
    <StyledAboutTab>
      {error && (
        <Alert
          type="warning"
          message={error}
          showIcon
          className="about-alert"
        />
      )}

      <div className="about-layout">
        <section className="about-main">
          <div className="about-section">
            <div className="about-steps-header">
              <Typography.Title level={5} className="about-section-title">
                Onboarding Steps
              </Typography.Title>
              <div className="about-progress-wrap">
                <span className="about-progress-text">
                  {completedCount} of {steps.length} complete
                </span>
                <Progress
                  percent={Math.round((completedCount / steps.length) * 100)}
                  showInfo={false}
                  strokeColor={PH.primary}
                  className="about-progress-bar"
                />
              </div>
            </div>
            {allComplete && (
              <div className="about-all-set">
                <SmileOutlined className="about-all-set-icon" />
                <span>All set! Your POS integration is ready.</span>
              </div>
            )}
            <p className="about-section-intro">
              Complete these steps to get the most out of your POS integration. You can finish everything now or come back later.
            </p>
            <ul className="about-steps">
              {steps.map((step, index) => (
                <li key={step.key} className={`about-step about-step--${step.status}`}>
                  <span className="about-step-number">{index + 1}</span>
                  <div className="about-step-icon">
                    {renderStepIcon(step.status)}
                  </div>
                  <div className="about-step-content">
                    <div className="about-step-header">
                      <span className="about-step-title">{step.title}</span>
                      {renderStepStatusLabel(step.status)}
                    </div>
                    <p className="about-step-description">{step.description}</p>
                    {(step.status === STEP_STATUS.TODO || step.status === STEP_STATUS.LATER) && onGoToTab && (
                      <Button
                        type={step.status === STEP_STATUS.TODO ? 'primary' : 'default'}
                        size="small"
                        className="about-step-cta"
                        onClick={() => onGoToTab(step.tab)}
                      >
                        {step.cta}
                        <RightOutlined />
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <aside className="about-sidebar">
          <section className="about-section">
            <Typography.Title level={5} className="about-section-title">
              Integration Details
            </Typography.Title>
            <div className="about-details-card">
              <Descriptions column={1} size="middle" bordered className="about-descriptions">
                <Descriptions.Item label="Restaurant">
                  {merchant?.name ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={STATUS_COLORS[status?.status] ?? 'default'}>
                    {status?.status?.replace(/_/g, ' ') ?? 'Not connected'}
                  </Tag>
                  {status?.has_errors && <Tag color="red">Errors</Tag>}
                </Descriptions.Item>
                {status?.location_id && (
                  <Descriptions.Item label="Location ID">
                    <code>{status.location_id}</code>
                  </Descriptions.Item>
                )}
                {status?.account_id && (
                  <Descriptions.Item label="Account ID">
                    <code>{status.account_id}</code>
                  </Descriptions.Item>
                )}
                {status?.last_menu_sync && (
                  <Descriptions.Item label="Last Catalog Sync">
                    {new Date(status.last_menu_sync).toLocaleString()}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </div>
          </section>
        </aside>
      </div>
    </StyledAboutTab>
  );
}

export default AboutTab;
